import type React from 'react';
import channelLogisticsLogo from '@/assets/icons/channel-logistics-logo.png';
import codeIcon from '@/assets/icons/code.png';
import lockIcon from '@/assets/icons/lock.png';
import { ActionButton } from '@/components/atoms/primitives';
import type { ResetPasswordStageProps } from './ResetPasswordStage.types';

export const ResetPasswordStage: React.FC<ResetPasswordStageProps> = ({ onSubmit, error, onReturn }) => {
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = event.target as typeof event.target & {
      code: { value: string };
      newPassword: { value: string };
      confirmedNewPassword: { value: string };
    };
    onSubmit(target.code.value, target.newPassword.value, target.confirmedNewPassword.value);
  };

  return (
    <div className="flex flex-col items-center justify-center min-w-[600px] max-w-[600px] flex-shrink-0 h-full overflow-hidden relative">
      <div
        className="absolute right-4 bottom-4 w-[100px] h-[70px] bg-contain bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${channelLogisticsLogo})` }}
      />
      <form className="flex flex-col w-[400px] h-[500px] overflow-y-auto" onSubmit={handleSubmit} noValidate>
        <header>
          <h1 className="text-2xl text-white font-bold mb-8">Reset password</h1>
        </header>

        {error && (
          <div
            id="reset-password-error"
            className="text-brand-error font-bold -mt-4 mb-4 text-center"
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            {error}
          </div>
        )}

        <p className="text-gray-400 -mt-4 text-left" aria-live="polite">
          A reset code has been sent to your email.
        </p>

        <section aria-labelledby="reset-password-form-heading">
          <h2 id="reset-password-form-heading" className="sr-only">
            Reset password credentials
          </h2>
          <fieldset className="border-0 p-0 m-0">
            <legend className="sr-only">Enter reset code and new password</legend>

            <div>
              <label htmlFor="reset-code" className="text-base text-white font-bold mb-2.5 block">
                6-digit code
              </label>
              <div className="flex items-center max-w-full h-[50px] border border-gray-500 rounded-2xl px-4 mb-6">
                <span
                  className="w-6 h-6 bg-cover filter invert-[90%]"
                  style={{ backgroundImage: `url(${codeIcon})` }}
                  aria-hidden="true"
                />
                <input
                  id="reset-code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  className="w-[300px] ml-4 bg-transparent border-none text-white focus:outline-none"
                  placeholder="code"
                  required
                  autoComplete="one-time-code"
                  aria-describedby={error ? 'reset-password-error' : undefined}
                  aria-label="6-digit reset code"
                />
              </div>
            </div>

            <div>
              <label htmlFor="reset-new-password" className="text-base text-white font-bold mb-2.5 block">
                New password
              </label>
              <div className="flex items-center max-w-full h-[50px] border border-gray-500 rounded-2xl px-4 mb-6">
                <span
                  className="w-6 h-6 bg-cover filter invert"
                  style={{ backgroundImage: `url(${lockIcon})` }}
                  aria-hidden="true"
                />
                <input
                  id="reset-new-password"
                  name="newPassword"
                  type="password"
                  className="w-[300px] ml-4 bg-transparent border-none text-white focus:outline-none"
                  placeholder="new password"
                  required
                  autoComplete="new-password"
                  aria-describedby={error ? 'reset-password-error' : undefined}
                  minLength={8}
                />
              </div>
            </div>

            <div>
              <label htmlFor="reset-confirm-password" className="text-base text-white font-bold mb-2.5 block">
                Confirm new password
              </label>
              <div className="flex items-center max-w-full h-[50px] border border-gray-500 rounded-2xl px-4 mb-6">
                <span
                  className="w-6 h-6 bg-cover filter invert"
                  style={{ backgroundImage: `url(${lockIcon})` }}
                  aria-hidden="true"
                />
                <input
                  id="reset-confirm-password"
                  name="confirmedNewPassword"
                  type="password"
                  className="w-[300px] ml-4 bg-transparent border-none text-white focus:outline-none"
                  placeholder="confirm new password"
                  required
                  autoComplete="new-password"
                  aria-describedby={error ? 'reset-password-error' : undefined}
                  minLength={8}
                />
              </div>
            </div>
          </fieldset>
        </section>

        <section aria-label="Form actions">
          <button
            type="submit"
            className="flex items-center justify-center w-full h-[50px] text-white border-none rounded bg-brand-primary text-base font-bold transition duration-[0.25s] hover:cursor-pointer"
          >
            Reset password
          </button>
        </section>

        <footer>
          <p className="text-white mt-4 text-center">
            <ActionButton variant="back" onClick={onReturn} className="w-full text-center align-center">
              Return
            </ActionButton>
          </p>
        </footer>
      </form>
    </div>
  );
};
